import { useEffect, useState } from "react";
import { X } from "lucide-react";

// Static legal copy. Kept in the same module on purpose — the agent never wants this
// split across files because a future translator/legal review needs to see all copy in
// one place. Email is left as "[TU EMAIL]" verbatim per spec; owner fills it in later.
const PRIVACY_TITLE = "AVISO DE PRIVACIDAD — NegoIA";
const PRIVACY_BODY = `NegoIA recopila únicamente el nombre de tu negocio y ciudad para personalizar las estrategias generadas. No vendemos ni compartimos tus datos con terceros.

Los datos se almacenan localmente en tu dispositivo y no son transmitidos a servidores externos.

Consultamos festivos nacionales a través de date.nager.at. Ningún dato personal se comparte con este servicio.

Consultamos el pronóstico del tiempo a través de open-meteo.com usando la ciudad de tu perfil. Ningún dato personal se comparte.

Las gráficas se generan a través de quickchart.io usando únicamente los datos numéricos de ventas que tú registras, sin información personal.

Consultamos el directorio oficial de negocios DENUE del INEGI usando la ciudad de tu perfil para mostrar negocios similares cercanos.

También consultamos: open-meteo.com (clima), quickchart.io (gráficas), date.nager.at (festivos) e inegi.org.mx/DENUE (directorio de negocios). Solo reciben datos técnicos mínimos sin información personal identificable.

Para el módulo de Diagnóstico, recopilamos tu nombre y número de WhatsApp únicamente para enviarte tu reporte personalizado y, si tú lo solicitas, contactarte sobre servicios de consultoría. Nunca compartimos tu información con terceros. Puedes solicitar la eliminación de tus datos en cualquier momento escribiendo a [negoia.contacto@gmail.com].

Puedes dejar de usar la app en cualquier momento. Para dudas o solicitudes de eliminación de datos escríbenos a:
negoia.contacto@gmail.com

Responsable: Jesús Eduardo Reyes Jacinto
Acapulco, Guerrero, México · 2026`;

const TERMS_TITLE = "TÉRMINOS DE USO — NegoIA";
const TERMS_BODY = `NegoIA es una herramienta de apoyo para marketing de pequeños negocios. Los textos y estrategias generados son sugerencias — el usuario es responsable del contenido que publique.

NegoIA no garantiza resultados específicos de ventas o crecimiento. Los resultados dependen de la ejecución del usuario.

El uso de la app implica aceptación de estos términos.

Servicio proporcionado por:
Jesús Eduardo Reyes Jacinto
Acapulco, Guerrero, México · 2026`;

function LegalModal({ title, body, onClose }) {
  // Esc closes; effect cleans up on unmount or when open === false (component unmounts)
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
      onClick={onClose}
      data-testid="legal-modal-backdrop"
    >
      <div
        className="relative bg-white rounded-2xl p-6 w-full max-w-md max-h-[85vh] overflow-y-auto shadow-2xl animate-scale-in"
        onClick={(e) => e.stopPropagation()}
        data-testid="legal-modal"
        role="dialog"
        aria-modal="true"
      >
        <button
          type="button"
          onClick={onClose}
          data-testid="legal-modal-close"
          aria-label="Cerrar"
          className="absolute top-3 right-3 w-9 h-9 inline-flex items-center justify-center rounded-full text-slate-500 active:bg-slate-100 active:text-slate-800 transition-all"
        >
          <X className="w-5 h-5" strokeWidth={2.4} />
        </button>
        <h2
          className="text-base font-extrabold tracking-tight text-slate-900 pr-8 mb-4"
          data-testid="legal-modal-title"
        >
          {title}
        </h2>
        <p
          className="text-sm text-slate-700 leading-relaxed whitespace-pre-line"
          data-testid="legal-modal-body"
        >
          {body}
        </p>
      </div>
    </div>
  );
}

export default function Footer() {
  const [openModal, setOpenModal] = useState(null); // null | "privacy" | "terms"
  const close = () => setOpenModal(null);

  return (
    <footer
      className="px-6 pt-6 pb-8 text-center"
      data-testid="app-footer"
    >
      <p
        className="leading-snug text-slate-400"
        style={{ fontSize: "11px" }}
        data-testid="footer-copyright"
      >
        © 2026 NegoIA · Todos los derechos reservados
      </p>
      <p
        className="leading-snug text-slate-400 mt-1"
        style={{ fontSize: "11px" }}
      >
        <button
          type="button"
          onClick={() => setOpenModal("privacy")}
          data-testid="footer-privacy-link"
          className="underline underline-offset-2 active:text-slate-600 transition-colors"
        >
          Aviso de Privacidad
        </button>
        <span className="mx-1">·</span>
        <button
          type="button"
          onClick={() => setOpenModal("terms")}
          data-testid="footer-terms-link"
          className="underline underline-offset-2 active:text-slate-600 transition-colors"
        >
          Términos de uso
        </button>
      </p>

      {openModal === "privacy" && (
        <LegalModal title={PRIVACY_TITLE} body={PRIVACY_BODY} onClose={close} />
      )}
      {openModal === "terms" && (
        <LegalModal title={TERMS_TITLE} body={TERMS_BODY} onClose={close} />
      )}
    </footer>
  );
}
